// ResourcesTab.js
// Extracted from AIWorkforcePage.js for better maintainability

import { TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { 
  Upload, Plus, Link, Bot, FolderOpen, LayoutGrid, RefreshCw, Eye, ExternalLink, Download 
} from 'lucide-react';

export const ResourcesTab = ({
  resources = [],
  linkedAssets = [],
  resourceStats,
  resourceViewMode,
  onViewModeChange,
  resourceFilter,
  onFilterChange,
  onRefresh,
  onUploadResource,
  onImportFromAssets,
  onResourceClick
}) => {
  const viewModes = [
    { value: 'ai_library', label: 'AI Resources', icon: Bot },
    { value: 'asset_library', label: 'Asset Library', icon: FolderOpen },
    { value: 'combined', label: 'All Resources', icon: LayoutGrid }
  ];

  const categories = ['all', 'product', 'logo', 'marketing', 'background', 'other'];

  const renderResourceCard = (resource, sourceType) => (
    <Card 
      key={resource.id} 
      className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
      onClick={() => onResourceClick({ ...resource, source_type: sourceType })}
      data-testid={`resource-${resource.id}`}
    >
      <div className="aspect-square bg-slate-100 relative">
        <img 
          src={resource.file_url || `data:image/${resource.file_type};base64,${resource.base64_data}`}
          alt={resource.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <Button variant="secondary" size="sm">
            <Eye className="h-4 w-4 mr-1" /> View
          </Button>
        </div>
        <Badge className="absolute top-2 right-2 bg-indigo-600">
          {resource.category}
        </Badge>
        {sourceType === 'ai_library' && (
          <Badge className="absolute top-2 left-2 bg-violet-600 text-xs">
            <Bot className="h-3 w-3" />
          </Badge>
        )}
        {sourceType === 'asset_library' && (
          <Badge className="absolute top-2 left-2 bg-blue-600 text-xs">
            <FolderOpen className="h-3 w-3" />
          </Badge>
        )}
      </div>
      <CardContent className="p-3">
        <p className="font-medium truncate" title={resource.name}>{resource.name}</p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-muted-foreground">
            {resource.file_type?.toUpperCase()} • {resource.file_size ? `${(resource.file_size / 1024).toFixed(1)}KB` : 'N/A'}
          </span>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <TabsContent value="resources" className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center">
            <Upload className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-indigo-900">Resource Library</h2>
            <p className="text-indigo-700 mt-1">
              Access AI resources and assets from your Asset Library. AI agents automatically use these when creating content.
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={onImportFromAssets}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
            >
              <Link className="h-4 w-4 mr-2" />
              Import from Assets
            </Button>
            <Button onClick={onUploadResource} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-2" />
              Upload Resource
            </Button>
          </div>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2 items-center">
          <Label className="mr-2 font-medium">View:</Label>
          <div className="flex rounded-lg border p-1 bg-slate-50">
            {viewModes.map(({ value, label, icon: Icon }) => (
              <Button
                key={value}
                variant={resourceViewMode === value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onViewModeChange(value)}
                className={resourceViewMode === value ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
              >
                <Icon className="h-4 w-4 mr-1" />
                {label}
              </Button>
            ))}
          </div>
        </div>
        
        {resourceStats && (
          <div className="flex gap-3 text-sm">
            {resourceViewMode === 'combined' ? (
              <>
                <Badge variant="outline" className="bg-violet-50">
                  <Bot className="h-3 w-3 mr-1" />
                  AI: {resourceStats.ai_library_count || 0}
                </Badge>
                <Badge variant="outline" className="bg-blue-50">
                  <FolderOpen className="h-3 w-3 mr-1" />
                  Assets: {resourceStats.asset_library_count || 0}
                </Badge>
              </>
            ) : (
              <Badge variant="outline" className="bg-indigo-50">
                Total: {resourceStats.total || 0}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Category Filters */}
      <div className="flex gap-2 items-center">
        <Label className="mr-2">Filter by category:</Label>
        {categories.map((cat) => (
          <Button
            key={cat}
            variant={resourceFilter === cat ? 'default' : 'outline'}
            size="sm"
            onClick={() => onFilterChange(cat)}
            className={resourceFilter === cat ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
          >
            {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
          </Button>
        ))}
        <Button variant="outline" size="sm" onClick={onRefresh} className="ml-auto">
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Resources Grid */}
      {resourceViewMode === 'ai_library' && (
        resources.length === 0 ? (
          <Card className="p-12">
            <div className="text-center">
              <Upload className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground">No AI Resources Yet</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Upload product photos and marketing assets for AI agents to use.
              </p>
              <div className="flex gap-2 justify-center mt-4">
                <Button variant="outline" onClick={onImportFromAssets}>
                  <Link className="h-4 w-4 mr-2" />
                  Import from Assets
                </Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={onUploadResource}>
                  <Plus className="h-4 w-4 mr-2" />
                  Upload Resource
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {resources.map((resource) => renderResourceCard(resource, 'ai_library'))}
          </div>
        )
      )}

      {resourceViewMode === 'asset_library' && (
        linkedAssets.length === 0 ? (
          <Card className="p-12">
            <div className="text-center">
              <FolderOpen className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground">No Assets in Library</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Go to the Asset Repository to upload company assets.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => window.location.href = '/assets'}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Asset Repository
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {linkedAssets.map((asset) => renderResourceCard(asset, 'asset_library'))}
          </div>
        )
      )}

      {resourceViewMode === 'combined' && (
        <div className="space-y-6">
          {resources.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Bot className="h-5 w-5 text-violet-600" />
                AI Resource Library ({resources.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {resources.map((resource) => renderResourceCard(resource, 'ai_library'))}
              </div>
            </div>
          )}
          {linkedAssets.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-blue-600" />
                Asset Library ({linkedAssets.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {linkedAssets.map((asset) => renderResourceCard(asset, 'asset_library'))}
              </div>
            </div>
          )}
          {resources.length === 0 && linkedAssets.length === 0 && (
            <Card className="p-12">
              <div className="text-center">
                <LayoutGrid className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">No Resources Available</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload resources to the AI library or import from your Asset Repository.
                </p>
              </div>
            </Card>
          )}
        </div>
      )}
    </TabsContent>
  );
};

export default ResourcesTab;
